import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import { useAuthStore } from "../features/auth/store.ts";
import { InterviewListScreen } from "../features/interview/InterviewListScreen.tsx";
import { isAdminEmail } from "../lib/admins.ts";

export function InterviewGuard() {
  const nav = useNavigate();
  const user = useAuthStore((s) => s.user);
  const email = useAuthStore((s) => s.user?.email ?? s.email);
  const initialized = useAuthStore((s) => s.initialized);

  const isAdmin = isAdminEmail(email);
  // The server profile (which carries `interviewAccess`) is fetched async in
  // `_app.tsx` and is deliberately NOT persisted, so `user` is null for the
  // first moment after every page load/reload — even for users who DO have
  // access. `initialized` only tracks Firebase auth, which resolves earlier.
  // We must therefore wait for the profile before deciding, otherwise a
  // legitimate interview user is bounced to /dashboard during that window and
  // "the interview section disappears". Admins are known from the persisted
  // email alone, so they never have to wait.
  const profileLoaded = user !== null;
  const allowed = Boolean(user?.interviewAccess) || isAdmin;

  useEffect(() => {
    // Only redirect once we're certain the user lacks access (profile loaded
    // and no flag, or a non-admin) — never while the profile is still loading.
    if (initialized && (profileLoaded || isAdmin) && !allowed) {
      nav({ to: "/dashboard" });
    }
  }, [initialized, profileLoaded, isAdmin, allowed, nav]);

  if (allowed) return <InterviewListScreen />;
  // Access not yet decided: show a light placeholder instead of redirecting.
  if (!profileLoaded) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>Loading…</div>
    );
  }
  return null;
}

export const Route = createFileRoute("/_app/interviews/")({
  component: InterviewGuard,
});
